import {
  DISCORD_LOGIN_REGISTRATION_GUARD_CONTROL,
  DISCORD_OIDC_CONSENT_CONTROL,
  IdentityBrowserContractError,
} from "./kratos-browser-contract.js";
import { element, translate } from "./identity-view-primitives.js";
import {
  MAX_ACCOUNT_EMAIL_ADDRESSES,
  MAX_ACCOUNT_EMAIL_LENGTH,
} from "./email-address-contract.js";
import {
  requiredLegalPublication,
  allowsDraftLegalAcceptance,
  LEGAL_ACCEPTANCE_FIELDS,
  hasRequiredLegalDocuments,
  legalDocumentHrefs,
} from "./legal-acceptance-contract.js";

const INTERNAL_PROVIDER_COPY = /\b(?:ory|kratos|self-service|flows?)\b/i;
const LAST_SIGN_IN_METHOD =
  /^(?:can not unlink OpenID Connect connection because it is the|can not remove the) last remaining first factor credential$/i;
const FIRST_FACTOR_REMOVAL_CONTROLS = new Set([
  "password_unlink",
  "unlink",
  "passkey_remove",
]);

function safeProviderCopy(message) {
  const text = String(message?.text ?? "").trim();
  if (LAST_SIGN_IN_METHOD.test(text)) {
    return translate(
      "account.error.last_sign_in_method",
      "This is your only sign-in method. Add another one before removing it.",
    );
  }
  if (text && !INTERNAL_PROVIDER_COPY.test(text)) {
    return text;
  }
  return message?.type === "error"
    ? translate(
        "account.message.request_failed",
        "We couldn’t complete that request. Check the details and try again.",
      )
    : translate("account.message.continue", "Continue to finish this account step.");
}

export function accountFlowMessageText(message) {
  return safeProviderCopy(message);
}

export function accountFirstFactorRemovalHelp(method) {
  const blocked = method?.nodes?.some((control) => (
    control?.kind === "input"
    && control.disabled === true
    && FIRST_FACTOR_REMOVAL_CONTROLS.has(control.name)
  ));
  return blocked
    ? translate(
        "account.settings.first_factor_removal_blocked",
        "This is your only sign-in method. Add another one before removing it.",
      )
    : "";
}

function appendMessages(root, messages) {
  if (!messages.length) {
    return;
  }
  const hasError = messages.some((message) => message.type === "error");
  const list = element("ul", {
    className: hasError
      ? "grid gap-2 rounded-box border border-error/25 bg-error/8 p-3 text-sm text-error"
      : "grid gap-2 rounded-box border border-base-300 bg-base-200/60 p-3 text-sm",
  });
  list.setAttribute(hasError ? "role" : "aria-live", hasError ? "alert" : "polite");
  for (const message of messages) {
    const item = element("li", { text: safeProviderCopy(message) });
    item.dataset.messageType = message.type;
    list.append(item);
  }
  root.append(list);
}

function fieldMessages(control, id) {
  if (!control.messages.length) {
    return null;
  }
  const messages = element("div", { className: "grid gap-1 text-xs text-error" });
  messages.id = id;
  for (const message of control.messages) {
    const line = element("p", { text: safeProviderCopy(message) });
    line.dataset.messageType = message.type;
    messages.append(line);
  }
  return messages;
}

function nodeMessages(messages) {
  if (!messages.length) {
    return null;
  }
  const hasError = messages.some((message) => message.type === "error");
  const root = element("div", {
    className: hasError ? "grid gap-1 text-xs text-error" : "grid gap-1 text-xs text-base-content/65",
  });
  root.setAttribute("role", hasError ? "alert" : "status");
  for (const message of messages) {
    root.append(element("p", { text: safeProviderCopy(message) }));
  }
  return root;
}

function fieldLabel(control) {
  const labels = {
    identifier: ["account.field.identifier", "Account handle or recovery email"],
    "traits.username": ["account.field.handle", "Account handle"],
    "traits.display_name": ["account.field.display_name", "Display name"],
    password: ["account.field.password", "Password"],
    email: ["account.field.recovery_email", "Recovery email"],
    code: ["account.field.email_code", "Email code"],
    recovery_address: ["account.field.recovery_email", "Recovery email"],
    recovery_confirm_address: ["account.field.recovery_email", "Recovery email"],
    totp_code: ["account.field.authenticator_code", "Authenticator code"],
    lookup_secret: ["account.field.backup_code", "Backup code"],
    webauthn_register_displayname: ["account.field.security_key_name", "Security key name"],
  };
  const label = labels[control.name];
  if (label) {
    return translate(label[0], label[1]);
  }
  const providerLabel = String(control.label ?? "").trim();
  return providerLabel && !INTERNAL_PROVIDER_COPY.test(providerLabel)
    ? providerLabel
    : control.name;
}

function fieldAutocomplete(control, view) {
  if (["identifier", "traits.username"].includes(control.name)) {
    return "username";
  }
  if (control.name === "traits.display_name") {
    return "name";
  }
  if ([
    "traits.emails",
    "email",
    "recovery_address",
    "recovery_confirm_address",
  ].includes(
    control.name,
  )) {
    return "email";
  }
  if (control.name === "password") {
    return view === "login" ? "current-password" : "new-password";
  }
  if (["code", "totp_code"].includes(control.name)) {
    return "one-time-code";
  }
  if (control.name === "lookup_secret") {
    return "off";
  }
  return "";
}

function controlInput(
  control,
  {
    messageId,
    view,
    required = control.required,
    labelText = fieldLabel(control),
    helpText = "",
  },
) {
  const checkbox = ["checkbox", "radio"].includes(control.type);
  const wrapper = element(checkbox ? "label" : "fieldset", {
    className: checkbox
      ? "label w-fit cursor-pointer justify-start gap-3"
      : "fieldset min-w-0 grid-cols-1 gap-2",
  });
  wrapper.dataset.accountControlName = control.name;
  const label = element(checkbox ? "span" : "label", {
    className: checkbox ? "font-medium" : "label min-w-0 whitespace-normal",
    text: labelText,
  });
  const authenticatorCode = control.name === "totp_code" && control.maxlength === 6;
  const input = element("input", {
    className: checkbox
      ? control.type === "radio"
        ? "radio radio-primary radio-sm"
        : "checkbox checkbox-primary checkbox-sm"
      : authenticatorCode
        ? "tabular-nums"
        : view === "registration"
          ? "input min-w-0 w-full"
          : "input input-sm min-w-0 w-full",
  });
  input.name = control.name;
  input.type = control.type;
  if (!checkbox) {
    input.id = `${messageId}-control`;
    label.htmlFor = input.id;
  }
  input.required = required;
  input.disabled = control.disabled;
  if (checkbox) {
    input.value = control.value || "true";
    input.checked = ["true", "on", "1"].includes(control.value);
  } else {
    input.value = control.value;
  }
  input.autocomplete = control.autocomplete || fieldAutocomplete(control, view);
  if ([
    "identifier",
    "traits.username",
    "traits.emails",
    "email",
  ].includes(control.name)) {
    input.autocapitalize = "none";
    input.spellcheck = false;
  }
  if (["code", "totp_code"].includes(control.name)) {
    input.inputMode = "numeric";
  }
  if (control.maxlength) {
    input.maxLength = control.maxlength;
  }
  if (control.pattern) {
    input.pattern = control.pattern;
  }
  const messages = fieldMessages(control, messageId);
  if (messages) {
    input.setAttribute("aria-describedby", messageId);
    if (control.messages.some((message) => message.type === "error")) {
      input.setAttribute("aria-invalid", "true");
    }
  }
  if (checkbox) {
    wrapper.append(input, label);
  } else {
    wrapper.append(label);
    if (authenticatorCode) {
      const otp = element("label", { className: "otp otp-sm w-fit max-w-full" });
      for (let index = 0; index < control.maxlength; index += 1) {
        otp.append(element("span"));
      }
      otp.append(input);
      wrapper.append(otp);
    } else {
      wrapper.append(input);
    }
  }
  if (messages) {
    wrapper.append(messages);
  }
  if (helpText) {
    wrapper.append(element("span", {
      className: "min-w-0 text-sm leading-5 text-base-content/60",
      text: helpText,
    }));
  }
  return wrapper;
}

function buttonCopy(control, view, { awaitingCode = false } = {}) {
  const providerLabel = String(control.label ?? "").trim();
  if (
    ["recovery_select_address", "recovery_confirm_address"].includes(
      control.name,
    )
    && providerLabel
    && !INTERNAL_PROVIDER_COPY.test(providerLabel)
  ) {
    return providerLabel;
  }
  const byName = {
    passkey_login_trigger: ["account.action.sign_in_passkey", "Sign in with a passkey"],
    passkey_register_trigger: view === "settings"
      ? ["account.action.add_passkey", "Add a passkey"]
      : ["account.action.register_passkey", "Register with a passkey"],
    passkey_remove: ["account.action.remove_passkey", "Remove passkey"],
    password_unlink: ["account.action.remove_password", "Remove password"],
    webauthn_register_trigger: ["account.action.add_security_key", "Add security key"],
    webauthn_login_trigger: ["account.action.use_security_key", "Use security key"],
    webauthn_remove: ["account.action.remove_security_key", "Remove security key"],
    provider: view === "registration"
      ? ["account.action.register_discord", "Register with Discord"]
      : ["account.action.sign_in_discord", "Sign in with Discord"],
    link: ["account.action.link_discord", "Link Discord"],
    unlink: ["account.action.unlink_discord", "Unlink Discord"],
    totp_unlink: ["account.action.remove_authenticator", "Remove authenticator app"],
    lookup_secret_reveal: ["account.action.show_backup_codes", "Show backup codes"],
    lookup_secret_regenerate: [
      "account.action.regenerate_backup_codes",
      "Generate new backup codes",
    ],
    lookup_secret_disable: ["account.action.remove_backup_codes", "Remove backup codes"],
    lookup_secret_confirm: ["account.action.confirm_backup_codes", "I’ve saved these codes"],
    recovery_select_address: ["account.action.send_code", "Send code"],
    recovery_confirm_address: ["account.action.resend_code", "Send a new code"],
    email: ["account.action.resend_code", "Send a new code"],
    screen: ["account.action.back", "Back"],
  };
  let copy = byName[control.name];
  if (!copy && control.value === "profile") {
    copy = ["account.action.save_profile", "Save profile"];
  } else if (!copy && control.value === "password") {
    copy = view === "login"
      ? ["account.action.submit", "Sign in"]
      : view === "registration"
        ? ["account.action.register", "Create account"]
        : ["account.action.change_password", "Change password"];
  } else if (!copy && control.value === "totp") {
    copy = view === "settings"
      ? ["account.action.enable_authenticator", "Verify and enable"]
      : ["account.action.verify", "Verify"];
  } else if (!copy && control.value === "lookup_secret") {
    copy = ["account.action.verify", "Verify"];
  } else if (!copy && control.value === "code") {
    copy = awaitingCode
      ? ["account.action.verify", "Verify code"]
      : [
          view === "verification"
            ? "account.action.send_verification_code"
            : "account.action.send_recovery_code",
          view === "verification" ? "Send verification code" : "Send recovery code",
        ];
  }
  if (copy) {
    return translate(copy[0], copy[1]);
  }
  return providerLabel && !INTERNAL_PROVIDER_COPY.test(providerLabel)
    ? providerLabel
    : translate("account.action.continue", "Continue");
}

function accountIcon(href, className = "size-5 shrink-0") {
  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  svg.setAttribute("class", `fishy-icon ${className}`);
  svg.setAttribute("viewBox", "0 0 24 24");
  svg.setAttribute("aria-hidden", "true");
  const use = document.createElementNS("http://www.w3.org/2000/svg", "use");
  use.setAttribute("width", "100%");
  use.setAttribute("height", "100%");
  use.setAttribute("href", href);
  svg.append(use);
  return svg;
}

export function resendSkipsFormValidation(control) {
  return control?.type === "submit"
    && ["email", "recovery_confirm_address"].includes(control.name);
}

function controlButton(control, view, options) {
  const destructive = [
    "password_unlink",
    "passkey_remove",
    "webauthn_remove",
    "totp_unlink",
    "lookup_secret_disable",
    "lookup_secret_regenerate",
  ].includes(control.name);
  const secondary = (control.group === "oidc" && view !== "registration")
    || destructive
    || control.name === "screen";
  const copy = buttonCopy(control, view, options);
  const size = view === "registration" ? "" : " btn-sm";
  const width = view === "registration" ? " w-full sm:w-fit" : " w-fit";
  const button = element("button", {
    className: `${secondary ? "btn btn-outline" : "btn btn-primary"}${size}${width}`,
    text: control.group === "oidc" ? "" : copy,
  });
  if (control.group === "oidc") {
    button.append(accountIcon("#fishy-discord-fill"), element("span", { text: copy }));
  }
  button.type = control.type;
  button.name = control.name;
  button.value = control.value;
  button.formNoValidate = resendSkipsFormValidation(control);
  const confirmations = {
    password_unlink: [
      "account.confirm.remove_password",
      "Remove password sign-in from this account?",
    ],
    unlink: [
      "account.confirm.unlink_discord",
      "Disconnect Discord sign-in from this account?",
    ],
    passkey_remove: ["account.confirm.remove_passkey", "Remove this passkey?"],
    webauthn_remove: [
      "account.confirm.remove_security_key",
      "Remove this security key from your two-step verification methods?",
    ],
    totp_unlink: [
      "account.confirm.remove_authenticator",
      "Remove your authenticator app from this account?",
    ],
    lookup_secret_regenerate: [
      "account.confirm.regenerate_backup_codes",
      "Generate new backup codes? Your current backup codes will stop working.",
    ],
    lookup_secret_disable: [
      "account.confirm.remove_backup_codes",
      "Remove all backup codes from this account?",
    ],
  };
  const confirmation = confirmations[control.name];
  if (confirmation) {
    button.dataset.accountConfirm = translate(confirmation[0], confirmation[1]);
  }
  if (control.onclickTrigger) {
    button.dataset.oryWebauthnTrigger = control.onclickTrigger;
    button.dataset.oryDisabled = control.disabled ? "true" : "false";
    button.disabled = true;
  } else {
    button.disabled = control.disabled;
  }
  return button;
}

function hiddenInput(control) {
  const input = element("input");
  input.type = "hidden";
  input.name = control.name;
  input.value = control.value;
  input.disabled = control.disabled;
  return input;
}

function registrationLegalAcceptance(formIndex) {
  const locale = globalThis.document?.documentElement?.lang === "de-DE" ? "de-DE" : "en-US";
  const hrefs = legalDocumentHrefs(locale);
  const fieldset = element("fieldset", {
    className:
      "fieldset rounded-box border border-base-300 bg-base-200/55 p-4 sm:p-5",
  });
  fieldset.append(element("legend", {
    className: "fieldset-legend",
    text: translate("account.registration.legal_title", "Terms and privacy information"),
  }));
  if (!hasRequiredLegalDocuments()) {
    const message = element("p", {
      className: "text-sm leading-5 text-base-content/80",
      text: locale === "de-DE"
        ? "Für diese Umgebung sind noch keine Nutzungsbedingungen zur Annahme veröffentlicht. Die Registrierung ist derzeit nicht verfügbar. Bestehende Konten können sich weiterhin anmelden."
        : "No Terms are currently published for acceptance in this environment. Registration is unavailable. Existing accounts can still sign in.",
    });
    message.setAttribute("role", "status");
    fieldset.append(message);
    for (const [kind, label] of [["terms", "Terms of Service"], ["privacy", "Privacy Policy"]]) {
      const link = element("a", { className: "link", text: label });
      link.href = hrefs[kind];
      fieldset.append(link);
    }
    return fieldset;
  }
  if (allowsDraftLegalAcceptance()) {
    fieldset.append(element("p", {
      className: "text-sm leading-5 text-base-content/80",
      text: locale === "de-DE"
        ? "Testumgebung: Du kannst die ausgewählten Entwurfs-Nutzungsbedingungen annehmen, um Testkontofunktionen zu nutzen. Dies ist keine Annahme der Produktionsbedingungen."
        : "Test environment: you can accept the selected draft Terms to use test account features. This is not acceptance of production Terms.",
    }));
  }

  function acceptanceRow({ id, name, prefixKey, prefix, linkKey, linkText, href, version }) {
    const input = element("input");
    input.id = id;
    input.type = "checkbox";
    input.name = name;
    input.value = "true";
    input.required = true;
    input.autocomplete = "off";
    input.className = "checkbox checkbox-primary mt-0.5 shrink-0";

    const copy = element("span", {
      className: "min-w-0 leading-5 text-base-content/80",
    });
    const link = element("a", {
      className: "link",
      text: translate(linkKey, linkText),
    });
    link.href = href;
    link.target = "_blank";
    link.rel = "noopener";
    copy.append(
      globalThis.document.createTextNode(`${translate(prefixKey, prefix)} `),
      link,
      globalThis.document.createTextNode(` (${version}).`),
    );
    const row = element("label", {
      className: "flex w-full cursor-pointer items-start gap-3 rounded-box p-2",
    });
    row.htmlFor = id;
    row.append(input, copy);
    return row;
  }

  const privacy = element("p", {
    className: "text-sm leading-5 text-base-content/70",
  });
  const privacyLink = element("a", {
    className: "link",
    text: translate("account.registration.privacy_link", "Privacy Policy"),
  });
  privacyLink.href = hrefs.privacy;
  privacyLink.target = "_blank";
  privacyLink.rel = "noopener";
  privacy.append(
    globalThis.document.createTextNode(
      `${translate("account.registration.privacy_notice", "How FishyStuff processes account data is explained in the")} `,
    ),
    privacyLink,
    globalThis.document.createTextNode("."),
  );
  fieldset.append(
    acceptanceRow({
      id: `fishystuff-terms-acceptance-${formIndex}`,
      name: LEGAL_ACCEPTANCE_FIELDS.termsAccepted,
      prefixKey: "account.registration.terms_prefix",
      prefix: "I confirm that I am at least 18 years old and accept the",
      linkKey: "account.registration.terms_link",
      linkText: "Terms of Service",
      href: hrefs.terms,
      version: requiredLegalPublication().termsVersion,
    }),
    privacy,
    hiddenInput({
      name: LEGAL_ACCEPTANCE_FIELDS.termsVersion,
      value: requiredLegalPublication().termsVersion,
      disabled: false,
    }),
    hiddenInput({
      name: LEGAL_ACCEPTANCE_FIELDS.locale,
      value: locale,
      disabled: false,
    }),
  );
  return fieldset;
}

function passkeyCredentialName(control, index) {
  const name = String(control.passkeyName ?? "").trim();
  if (name && name.toLowerCase() !== "unnamed") return name;
  return `${translate("account.passkey.default_name", "Passkey")} ${index + 1}`;
}

function passkeyCredentialRow(control, index, view, options) {
  const defaultName = passkeyCredentialName(control, index);
  const savedName = options.passkeyLabels?.kind === "ready"
    ? String(options.passkeyLabels.labels?.[control.value] ?? "")
    : "";
  const row = element("div", {
    className:
      "grid min-w-0 gap-4 rounded-box border border-base-300 bg-base-200/35 p-4",
  });
  row.dataset.accountPasskey = "";
  row.dataset.accountPasskeyCredential = control.value;
  row.dataset.accountPasskeyDefaultName = defaultName;
  row.dataset.accountPasskeyLabel = savedName;
  const heading = element("div", {
    className: "flex min-w-0 flex-wrap items-center justify-between gap-3",
  });
  const name = element("span", {
    className: "min-w-0 truncate font-medium text-base-content",
    text: savedName || defaultName,
  });
  name.dataset.accountPasskeyName = "";
  const actions = element("div", {
    className: "flex flex-wrap items-center justify-end gap-2",
  });
  const rename = element("button", {
    className: "btn btn-sm",
    text: translate("account.action.rename_passkey", "Rename"),
  });
  rename.type = "button";
  rename.dataset.accountPasskeyLabelRename = "";
  actions.append(rename, controlButton(control, view, options));
  heading.append(name, actions);
  row.append(heading);
  return row;
}

function webauthnCredentialRow(control, index, view, options) {
  const name = String(control.webauthnName ?? "").trim()
    || `${translate("account.webauthn.default_name", "Security key")} ${index + 1}`;
  const row = element("div", {
    className:
      "flex min-w-0 flex-wrap items-center justify-between gap-3 rounded-box border border-base-300 bg-base-200/35 p-4",
  });
  row.dataset.accountWebauthnCredential = control.value;
  row.append(
    element("span", {
      className: "min-w-0 truncate font-medium text-base-content",
      text: name,
    }),
    controlButton(control, view, options),
  );
  return row;
}

function omitBlankTraits(form, names) {
  form.addEventListener("formdata", (event) => {
    for (const name of names) {
      const value = event.formData.get(name);
      if (typeof value === "string" && value.trim() === "") {
        event.formData.delete(name);
      }
    }
  });
}

function omitBlankEmailAddresses(form) {
  form.addEventListener("formdata", (event) => {
    const addresses = event.formData.getAll("traits.emails")
      .filter((value) => typeof value === "string" && value.trim() !== "");
    event.formData.delete("traits.emails");
    for (const address of addresses) event.formData.append("traits.emails", address);
  });
}

function indexedEmailControls(method) {
  return method.nodes
    .filter((node) => /^traits\.emails\.(?:0|[1-9][0-9]*)$/u.test(node.name))
    .sort((left, right) => (
      Number(left.name.slice("traits.emails.".length))
        - Number(right.name.slice("traits.emails.".length))
    ));
}

function displayNameControl(control, options) {
  const root = element("section", { className: "grid min-w-0 gap-2" });
  root.dataset.accountControlName = control.name;
  root.dataset.accountDisplayName = "";
  const field = controlInput(control, options);
  delete field.dataset.accountControlName;
  const panel = element("div", { className: "grid min-w-0 gap-2" });
  panel.dataset.accountDisplayNamePanel = "";
  const actions = element("div", {
    className: "flex min-w-0 justify-end pt-1",
  });
  const remove = element("button", {
    className: "btn btn-ghost btn-sm text-base-content/70 hover:text-error",
  });
  remove.type = "button";
  remove.dataset.accountDisplayNameRemove = "";
  remove.disabled = control.disabled;
  remove.append(
    accountIcon("#fishy-trash", "size-4 shrink-0"),
    element("span", {
      text: translate("account.action.remove_display_name", "Remove display name"),
    }),
  );
  actions.append(remove);
  field.append(actions);
  panel.append(field);
  const add = element("button", { className: "btn btn-soft btn-sm w-full" });
  add.type = "button";
  add.dataset.accountDisplayNameAdd = "";
  add.disabled = control.disabled;
  add.append(
    accountIcon("#fishy-user-4-fill", "size-4 shrink-0"),
    element("span", {
      text: translate("account.action.add_display_name", "Add display name"),
    }),
  );
  const active = String(control.value ?? "").trim() !== ""
    || control.messages.length > 0;
  panel.hidden = !active;
  add.hidden = active;
  root.append(panel, add);
  return root;
}

export function addAccountDisplayName(button) {
  const root = button?.closest?.("[data-account-display-name]");
  const panel = root?.querySelector?.("[data-account-display-name-panel]");
  const input = panel?.querySelector?.('input[name="traits.display_name"]');
  if (!root || !panel || !input || button.disabled) return false;
  panel.hidden = false;
  button.hidden = true;
  input.focus();
  return true;
}

export function removeAccountDisplayName(button) {
  const root = button?.closest?.("[data-account-display-name]");
  const panel = root?.querySelector?.("[data-account-display-name-panel]");
  const add = root?.querySelector?.("[data-account-display-name-add]");
  const input = panel?.querySelector?.('input[name="traits.display_name"]');
  if (!root || !panel || !add || !input || button.disabled) return false;
  input.value = "";
  panel.hidden = true;
  add.hidden = false;
  add.focus();
  return true;
}


function emailAddressRow({
  value = "",
  verification = "",
  messages = Object.freeze([]),
  disabled = false,
  fieldIdPrefix,
  rowId,
}) {
  const row = element("div", {
    className: "grid min-w-0 gap-2 rounded-box border border-base-300 bg-base-100 p-3",
  });
  row.dataset.accountEmailRow = "";
  const field = element("fieldset", { className: "fieldset min-w-0 grid-cols-1 gap-2" });
  const inputId = `${fieldIdPrefix}-email-${rowId}`;
  const label = element("label", {
    className: "label min-w-0 gap-2 whitespace-normal",
  });
  label.append(
    accountIcon("#fishy-mail-fill", "size-4 shrink-0"),
    element("span", { text: translate("account.email.address", "Email address") }),
  );
  label.htmlFor = inputId;
  const input = element("input", { className: "input input-sm min-w-0 w-full" });
  input.id = inputId;
  input.name = "traits.emails";
  input.type = "email";
  input.value = value;
  input.maxLength = MAX_ACCOUNT_EMAIL_LENGTH;
  input.autocomplete = "email";
  input.autocapitalize = "none";
  input.spellcheck = false;
  input.disabled = disabled;
  field.append(label, input);
  const fieldMessage = fieldMessages({ messages }, `${inputId}-messages`);
  if (fieldMessage) {
    input.setAttribute("aria-describedby", fieldMessage.id);
    if (messages.some((message) => message.type === "error")) {
      input.setAttribute("aria-invalid", "true");
    }
    field.append(fieldMessage);
  }
  row.append(field);

  const actions = element("div", {
    className: "flex min-w-0 flex-wrap items-center gap-2",
  });
  if (value && verification) {
    actions.append(element("span", {
      className: verification === "verified"
        ? "badge badge-success badge-soft badge-sm"
        : "badge badge-warning badge-soft badge-sm",
      text: verification === "verified"
        ? translate("account.email.verified", "Verified")
        : translate("account.email.unverified", "Not verified"),
    }));
    if (verification !== "verified") {
      const verify = element("button", {
        className: "btn btn-link btn-xs h-auto min-h-0 px-0",
        text: translate("account.action.verify_email", "Verify email"),
      });
      verify.type = "button";
      verify.dataset.accountVerifyEmail = value;
      actions.append(verify);
    }
  }
  const remove = element("button", {
    className: "btn btn-ghost btn-xs ml-auto text-base-content/70 hover:text-error",
  });
  remove.type = "button";
  remove.dataset.accountEmailRemove = "";
  remove.disabled = disabled;
  remove.append(
    accountIcon("#fishy-trash", "size-3.5 shrink-0"),
    element("span", {
      text: translate("account.action.remove_email", "Remove email"),
    }),
  );
  actions.append(remove);
  row.append(actions);
  return row;
}

function updateEmailCollectionLimit(collection) {
  const add = collection.querySelector("[data-account-email-add]");
  const count = collection.querySelectorAll("[data-account-email-row]").length;
  if (add) {
    add.disabled = collection.dataset.accountEmailDisabled === "true"
      || count >= MAX_ACCOUNT_EMAIL_ADDRESSES;
  }
}

function emailAddressCollection(method, {
  emailAddresses,
  fieldIdPrefix,
}) {
  const base = method.nodes.find((node) => node.name === "traits.emails");
  const indexed = indexedEmailControls(method);
  const addressStatus = new Map(emailAddresses.map((address) => [
    address.value,
    address.verification,
  ]));
  const collection = element("section", { className: "grid min-w-0 gap-3" });
  collection.dataset.accountControlName = "traits.emails";
  collection.dataset.accountEmailCollection = "";
  collection.dataset.accountEmailDisabled = base?.disabled === true ? "true" : "false";
  collection.dataset.accountEmailFieldPrefix = fieldIdPrefix;
  collection.dataset.accountEmailNextRow = String(Math.max(indexed.length, 1));
  const heading = element("div", {
    className: "grid min-w-0 gap-3 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center",
  });
  heading.append(element("h3", {
    className: "text-sm font-medium text-base-content",
    text: translate("account.email.title", "Email addresses"),
  }));
  const add = element("button", {
    className: "btn btn-soft btn-sm w-full sm:w-auto",
  });
  add.type = "button";
  add.dataset.accountEmailAdd = "";
  add.disabled = base?.disabled === true;
  add.append(
    accountIcon("#fishy-mail-fill", "size-4 shrink-0"),
    element("span", {
      text: translate("account.action.add_email", "Add email"),
    }),
  );
  heading.append(add);
  collection.append(
    heading,
    element("p", {
      className: "min-w-0 text-sm leading-5 text-base-content/60",
      text: translate(
        "account.field.recovery_email_help",
        "Keep at least one verified address so you can recover your account by email.",
      ),
    }),
  );
  const baseMessages = nodeMessages(base?.messages ?? []);
  if (baseMessages) collection.append(baseMessages);
  const rows = element("div", { className: "grid min-w-0 gap-3" });
  rows.dataset.accountEmailRows = "";
  const controls = indexed.length > 0
    ? indexed
    : String(base?.value ?? "").trim() !== "" || (base?.messages.length ?? 0) > 0
      ? [base]
      : [];
  for (const [index, control] of controls.entries()) {
    rows.append(emailAddressRow({
      value: control.value,
      verification: addressStatus.get(control.value) ?? "",
      messages: control.messages,
      disabled: base?.disabled === true || control.disabled === true,
      fieldIdPrefix,
      rowId: index,
    }));
  }
  collection.append(rows);
  updateEmailCollectionLimit(collection);
  return collection;
}

export function addAccountEmailAddressRow(button) {
  const collection = button?.closest?.("[data-account-email-collection]");
  const rows = collection?.querySelector?.("[data-account-email-rows]");
  if (!collection || !rows || button.disabled) return false;
  const count = rows.querySelectorAll("[data-account-email-row]").length;
  if (count >= MAX_ACCOUNT_EMAIL_ADDRESSES) return false;
  const rowId = Number(collection.dataset.accountEmailNextRow);
  if (!Number.isSafeInteger(rowId) || rowId < 0) return false;
  collection.dataset.accountEmailNextRow = String(rowId + 1);
  const row = emailAddressRow({
    fieldIdPrefix: collection.dataset.accountEmailFieldPrefix,
    rowId,
  });
  rows.append(row);
  updateEmailCollectionLimit(collection);
  row.querySelector('input[name="traits.emails"]')?.focus();
  return true;
}

export function removeAccountEmailAddressRow(button) {
  const collection = button?.closest?.("[data-account-email-collection]");
  const row = button?.closest?.("[data-account-email-row]");
  if (!collection || !row || button.disabled) return false;
  row.remove();
  updateEmailCollectionLimit(collection);
  collection.querySelector("[data-account-email-add]")?.focus();
  return true;
}

function flowTitle(flow) {
  if (flow.accountLinking) {
    return translate(
      "account.discord_handle_conflict.title",
      "Discord handle unavailable",
    );
  }
  if (flow.completion?.kind === "verificationPassed") {
    return translate("account.verification.complete_title", "Recovery email verified");
  }
  if (flow.view === "login" && flow.requestedAal === "aal2") {
    return translate("account.step_up.title", "Complete two-step verification");
  }
  if (flow.view === "login" && flow.refresh) {
    return translate("account.reauthenticate.title", "Confirm it’s you");
  }
  const titles = {
    login: ["account.login.title", "Sign in to FishyStuff"],
    registration: ["account.registration.title", "Create your FishyStuff account"],
    settings: ["account.settings.title", "Account settings"],
    recovery: ["account.recovery.title", "Recover your account"],
    verification: ["account.verification.title", "Verify your recovery email"],
  };
  const title = titles[flow.view] ?? ["account.error.title", "Account access problem"];
  return translate(title[0], title[1]);
}

function flowDetail(flow) {
  if (flow.accountLinking) {
    return translate(
      "account.discord_handle_conflict.detail",
      "That Discord handle is already used by a FishyStuff account. Sign in below to link Discord to that account, or create an account manually with a different handle.",
    );
  }
  if (flow.completion?.kind === "verificationPassed") {
    return translate(
      "account.verification.complete_detail",
      "Your recovery email is verified and can now be used to recover your account.",
    );
  }
  if (flow.view === "login" && flow.requestedAal === "aal2") {
    return translate(
      "account.step_up.detail",
      "Use a security key, your authenticator app, or a backup code to continue securely.",
    );
  }
  if (flow.view === "login" && flow.refresh) {
    return translate(
      "account.reauthenticate.detail",
      "Sign in again before changing sensitive account details.",
    );
  }
  const details = {
    recovery: [
      "account.recovery.detail",
      "We’ll send a short-lived code to the recovery email on your account.",
    ],
    verification: [
      "account.verification.detail",
      "Verify your recovery email so it can be trusted for account recovery.",
    ],
    settings: [
      "account.settings.detail",
      "Update your account settings.",
    ],
  };
  const detail = details[flow.view];
  return detail ? translate(detail[0], detail[1]) : "";
}

function methodTitle(view, method) {
  if (view === "login" && method === "passkey") {
    return translate("account.method.passkey_sign_in", "Passkey");
  }
  const titles = {
    passkey: ["account.method.passkey", "Passkeys"],
    webauthn: ["account.method.security_key", "Security key"],
    oidc: ["account.method.discord", "Discord"],
    profile: ["account.method.profile", "Account profile"],
    totp: ["account.method.authenticator", "Authenticator app"],
    lookup_secret: ["account.method.backup_codes", "Backup codes"],
    code: ["account.method.email_code", "Email code"],
  };
  if (titles[method]) {
    return translate(titles[method][0], titles[method][1]);
  }
  if (view === "login") {
    return translate("account.method.regular_sign_in", "Regular sign in");
  }
  return view === "settings"
    ? translate("account.method.password_change", "Password")
    : translate("account.method.password", "Handle and password");
}

function accessMethodPresentation(method) {
  const presentations = {
    password: {
      icon: "#fishy-user-lock-fill",
      label: translate("account.settings.navigation.password", "Password"),
    },
    oidc: {
      icon: "#fishy-discord-fill",
      label: translate("account.method.discord", "Discord"),
    },
    passkey: {
      icon: "#fishy-fingerprint-fill",
      label: translate("account.method.passkey_sign_in", "Passkey"),
    },
  };
  const presentation = presentations[method === "default" ? "password" : method];
  if (!presentation) throw new TypeError(`Unsupported account access method: ${method}`);
  return presentation;
}

function settingsMethodCopy(method) {
  const copy = {
    profile: [
      ["account.settings.navigation.profile", "Profile"],
      ["account.settings.profile.title", "Profile settings"],
      ["account.settings.profile.detail", "Update your profile information."],
    ],
    password: [
      ["account.settings.navigation.password", "Password"],
      ["account.settings.password.title", "Change password"],
      ["account.settings.password.detail", "Change or remove password sign-in."],
    ],
    oidc: [
      ["account.settings.navigation.oidc", "Social sign-in"],
      ["account.settings.oidc.title", "Connected accounts"],
      ["account.settings.oidc.detail", "Connect or disconnect Discord sign-in."],
    ],
    passkey: [
      ["account.settings.navigation.passkey", "Passkeys"],
      ["account.settings.passkey.title", "Manage passkeys"],
      ["account.settings.passkey.detail", "Add or remove passkeys for this account."],
    ],
    webauthn: [
      ["account.settings.navigation.webauthn", "Security keys"],
      ["account.settings.webauthn.title", "Security keys"],
      [
        "account.settings.webauthn.detail",
        "Manage device biometrics and hardware security keys for two-step verification.",
      ],
    ],
    totp: [
      ["account.settings.navigation.totp", "Authenticator app"],
      ["account.settings.totp.title", "Authenticator app"],
      ["account.settings.totp.detail", "Manage two-step verification with an authenticator app."],
    ],
    lookup_secret: [
      ["account.settings.navigation.lookup_secret", "Backup codes"],
      ["account.settings.lookup_secret.title", "Backup recovery codes"],
      ["account.settings.lookup_secret.detail", "Manage recovery codes for two-step verification."],
    ],
  };
  const selected = copy[method];
  if (!selected) throw new TypeError(`Unsupported settings method: ${method}`);
  const [navigation, title, detail] = selected;
  return Object.freeze({
    navigation: translate(navigation[0], navigation[1]),
    title: translate(title[0], title[1]),
    detail: translate(detail[0], detail[1]),
  });
}

function profileControl(controls, name) {
  return controls.querySelector(`[data-account-control-name="${name}"]`);
}

function profileNodeValue(method, name) {
  return method.nodes.find((node) => node.kind === "input" && node.name === name)?.value ?? "";
}

function arrangeProfileSettings(controls, method, {
  discordImagesAllowed = false,
} = {}) {
  const username = profileNodeValue(method, "traits.username");
  const displayName = profileNodeValue(method, "traits.display_name");
  const avatarUrl = profileNodeValue(method, "traits.avatar_url");
  const avatarColor = profileNodeValue(method, "traits.avatar_color");
  const handleControl = profileControl(controls, "traits.username");
  const displayNameControl = profileControl(controls, "traits.display_name");
  const emailControl = profileControl(controls, "traits.emails");
  const avatarPicker = controls.querySelector("fishy-account-avatar-picker");

  const summary = element("header", {
    className:
      "flex min-w-0 flex-col gap-4 border-b border-base-300 pb-6 sm:flex-row sm:items-center",
  });
  summary.dataset.accountProfileSummary = "";
  const avatar = element("fishy-account-avatar", { className: "block shrink-0" });
  avatar.setAttribute("value", avatarUrl);
  avatar.setAttribute("color", avatarColor);
  avatar.externalImagesAllowed = discordImagesAllowed;
  const identity = element("div", { className: "min-w-0" });
  identity.append(
    element("p", {
      className: "text-xs font-semibold uppercase tracking-wide text-base-content/55",
      text: translate("account.settings.navigation.profile", "Profile"),
    }),
    element("h2", {
      className: "mt-1 truncate text-xl font-semibold text-base-content",
      text: displayName || username,
    }),
    element("p", {
      className: "mt-1 truncate text-sm text-base-content/60",
      text: `@${username}`,
    }),
  );
  summary.append(avatar, identity);

  const details = element("section", { className: "grid min-w-0 gap-3" });
  details.append(element("h3", {
    className: "text-sm font-semibold text-base-content",
    text: translate("account.method.profile", "Profile details"),
  }));
  const detailFields = element("div", {
    className: "grid min-w-0 gap-5 rounded-box border border-base-300 bg-base-200/35 p-4 sm:p-5",
  });
  if (handleControl) detailFields.append(handleControl);
  if (displayNameControl) {
    if (handleControl) {
      displayNameControl.classList.add("border-t", "border-base-300", "pt-5");
    }
    detailFields.append(displayNameControl);
  }
  details.append(detailFields);

  const recovery = element("section", {
    className: "grid min-w-0 gap-4 border-t border-base-300 pt-6",
  });
  const addressRows = element("div", {
    className: "grid min-w-0 gap-4",
  });
  if (emailControl) addressRows.append(emailControl);
  recovery.append(addressRows);

  const fields = element("div", { className: "grid min-w-0 gap-6" });
  fields.append(details, recovery);
  const profileGrid = element("div", {
    className:
      "grid min-w-0 gap-8 lg:grid-cols-[minmax(0,1fr)_minmax(18rem,0.85fr)] lg:items-start",
  });
  profileGrid.append(fields);
  if (avatarPicker) profileGrid.append(avatarPicker);
  controls.className = "grid min-w-0 gap-8";
  controls.replaceChildren(summary, profileGrid);
}

function securityImage(node) {
  const figure = element("figure", {
    className: "grid w-fit gap-2 rounded-box border border-base-300 bg-white p-3 text-slate-900",
  });
  const image = element("img");
  image.src = node.src;
  image.width = node.width;
  image.height = node.height;
  image.alt = translate("account.totp.qr_alt", "Authenticator setup QR code");
  image.loading = "eager";
  image.decoding = "async";
  figure.append(image, element("figcaption", {
    className: "max-w-64 text-xs leading-5",
    text: translate(
      "account.totp.qr_help",
      "Scan this code with your authenticator app, then enter the generated code below.",
    ),
  }));
  return figure;
}

function securityText(node) {
  if (node.id === "totp_secret_key") {
    const root = element("div", { className: "grid gap-2" });
    root.append(
      element("p", {
        className: "text-sm text-base-content/70",
        text: translate(
          "account.totp.manual_help",
          "Can’t scan the QR code? Enter this setup key manually:",
        ),
      }),
      element("code", {
        className: "w-fit break-all rounded-box bg-base-200 px-3 py-2 font-mono text-sm",
        text: node.text.context.secret,
      }),
    );
    return root;
  }
  if (node.id === "lookup_secret_codes") {
    const root = element("div", { className: "grid gap-3" });
    root.append(element("p", {
      className: "alert alert-warning text-sm leading-6",
      text: translate(
        "account.backup_codes.save_notice",
        "Save these codes somewhere safe. Each available code can be used once if you lose your authenticator.",
      ),
    }));
    const list = element("ol", {
      className: "grid gap-2 rounded-box border border-base-300 bg-base-200/60 p-4 font-mono text-sm sm:grid-cols-2",
    });
    for (const code of node.text.context.codes) {
      const item = element("li", {
        className: code.kind === "used" ? "text-base-content/45 line-through" : "select-all",
        text: code.kind === "available"
          ? code.code
          : translate("account.backup_codes.used", "Used code"),
      });
      list.append(item);
    }
    root.append(list);
    return root;
  }
  return element("p", { className: "text-sm", text: safeProviderCopy(node.text) });
}

function flowNavigation(flow, context) {
  const links = element("nav", {
    className: "flex flex-wrap gap-x-4 gap-y-2 text-sm",
  });
  links.setAttribute("aria-label", translate("account.navigation", "Account access"));
  const add = (href, key, fallback, startFlow = "") => {
    const link = element("a", { className: "link link-primary w-fit", text: translate(key, fallback) });
    link.href = href;
    link.rel = "nofollow";
    if (startFlow) link.dataset.accountStartFlow = startFlow;
    links.append(link);
  };
  if (flow.view === "login" && flow.requestedAal !== "aal2" && !flow.refresh) {
    add(
      context.registrationStartUrl,
      flow.accountLinking ? "account.action.register_manually" : "account.action.need_account",
      flow.accountLinking ? "Create account manually" : "Need an account? Register",
      "registration",
    );
    add(
      context.recoveryStartUrl,
      "account.action.forgot_password",
      "Forgot your password?",
      "recovery",
    );
  } else if (flow.view === "registration") {
    add(
      context.loginStartUrl,
      "account.action.have_account",
      "Already registered? Sign in",
      "login",
    );
  } else if (flow.view === "recovery") {
    add(
      context.loginStartUrl,
      "account.action.back_to_sign_in",
      "Back to sign in",
      "login",
    );
  } else if (flow.view === "verification") {
    add(context.returnUrl, "account.action.back", "Back to account");
  }
  return links.childElementCount ? links : null;
}

export function createLoadingView() {
  const status = element("p", {
    className: "flex items-center gap-3 text-sm text-base-content/70",
    text: translate("account.state.loading", "Checking whether you’re signed in…"),
  });
  status.setAttribute("role", "status");
  status.setAttribute("aria-live", "polite");
  return status;
}

export function createSignedOutView(context) {
  const root = element("div", { className: "grid gap-5" });
  const status = element("div", { className: "grid gap-2" });
  status.append(
    element("h1", {
      className: "text-3xl font-bold tracking-tight text-base-content",
      text: translate("account.state.signed_out", "Not signed in"),
    }),
    element("p", {
      className: "text-sm leading-6 text-base-content/70",
      text: translate(
        "account.state.signed_out_detail",
        "Sign in to use synchronized settings and saved data on this device.",
      ),
    }),
    element("p", {
      className: "text-sm leading-6 text-base-content/70",
      text: translate(
        "account.registration.email_recovery_notice",
        "Recovery email is optional; without one, email recovery is unavailable.",
      ),
    }),
  );
  const actions = element("div", { className: "flex flex-wrap gap-3" });
  const addAction = (href, key, fallback, className, startFlow) => {
    const link = element("a", { className, text: translate(key, fallback) });
    link.href = href;
    link.rel = "nofollow";
    link.dataset.accountStartFlow = startFlow;
    actions.append(link);
  };
  addAction(
    context.loginStartUrl,
    "account.action.sign_in",
    "Sign in",
    "btn btn-primary w-fit",
    "login",
  );
  addAction(
    context.registrationStartUrl,
    "account.action.create",
    "Create account",
    "btn btn-outline w-fit",
    "registration",
  );
  addAction(
    context.recoveryStartUrl,
    "account.action.recover",
    "Recover account",
    "btn btn-ghost w-fit",
    "recovery",
  );
  root.append(status, actions);
  return root;
}

export function createFlowView(
  flow,
  context,
  fieldIdPrefix,
  {
    knownVerificationEmail = "",
    discordAvatarUrl = "",
    discordImagesAllowed = false,
    emailAddresses = Object.freeze([]),
    passkeyLabels = Object.freeze({ kind: "missing", schemaVersion: 1 }),
  } = {},
) {
  const settings = flow.view === "settings";
  const registration = flow.view === "registration";
  const loginChoices = flow.view === "login"
    && flow.requestedAal !== "aal2"
    && !flow.refresh
    && !flow.accountLinking;
  const root = element("div", {
    className: registration || loginChoices
      ? "mx-auto grid w-full max-w-3xl min-w-0 gap-6"
      : settings ? "grid min-w-0 gap-8" : "grid min-w-0 gap-6",
  });
  if (settings) {
    root.dataset.accountSettingsRoot = "";
    root.dataset.accountSettingsIdPrefix = fieldIdPrefix;
  }
  const heading = element("div", { className: "grid gap-2" });
  if (settings) heading.dataset.accountSettingsHeading = "";
  heading.append(element("h1", {
    className: "text-3xl font-bold tracking-tight text-base-content",
    text: flowTitle(flow),
  }));
  const detail = flowDetail(flow);
  if (detail) {
    heading.append(element("p", { className: "text-sm leading-6 text-base-content/70", text: detail }));
  }
  root.append(heading);
  if (!settings) appendMessages(root, flow.messages);

  const tabbedAccessMethods = registration || loginChoices;
  const methodsRoot = element("div", {
    className: tabbedAccessMethods
      ? "grid min-w-0 gap-4"
      : "grid min-w-0 grid-cols-1 gap-8",
  });
  if (settings) methodsRoot.dataset.accountSettingsMethods = "";

  const forms = flow.forms;
  const selectedAccessMethod = tabbedAccessMethods
    ? forms.find((method) => method.nodes.some((node) => (
        node.messages.some((message) => message.type === "error")
      )))?.method ?? forms[0]?.method
    : null;
  const accessTablist = tabbedAccessMethods && forms.length > 1
    ? element("div", {
        className:
          "tabs tabs-box tabs-md grid min-w-0 grid-cols-3 overflow-x-auto bg-base-200 p-1 shadow-sm",
      })
    : null;
  if (accessTablist) {
    accessTablist.setAttribute("role", "tablist");
    accessTablist.setAttribute("aria-label", flowTitle(flow));
    for (const [formIndex, method] of forms.entries()) {
      const presentation = accessMethodPresentation(method.method);
      const selected = method.method === selectedAccessMethod;
      const tab = element("button", {
        className:
          `tab min-w-0 gap-2 whitespace-nowrap px-3 sm:px-5${selected ? " tab-active" : ""}`,
      });
      tab.type = "button";
      tab.id = `${fieldIdPrefix}-access-${formIndex}-tab`;
      tab.dataset.accountAccessTab = method.method;
      tab.setAttribute("role", "tab");
      tab.setAttribute("aria-controls", `${fieldIdPrefix}-access-${formIndex}-panel`);
      tab.setAttribute("aria-selected", String(selected));
      tab.tabIndex = selected ? 0 : -1;
      tab.append(
        accountIcon(presentation.icon, "size-5 shrink-0"),
        element("span", { className: "truncate", text: presentation.label }),
      );
      accessTablist.append(tab);
    }
    methodsRoot.append(accessTablist);
  }

  forms.forEach((method, formIndex) => {
    const awaitingCode = method.nodes.some((node) => (
      node.kind === "input"
      && node.name === "code"
      && !["hidden", "submit", "button"].includes(node.type)
    ));
    const section = settings
      ? null
      : element("section", {
          className: tabbedAccessMethods
            ? "card card-border min-w-0 overflow-hidden bg-base-100 shadow-md"
            : "grid min-w-0 gap-4 rounded-box border border-base-300 p-4",
        });
    if (tabbedAccessMethods) {
      section.id = `${fieldIdPrefix}-access-${formIndex}-panel`;
      section.dataset.accountAccessPanel = method.method;
      if (accessTablist) {
        section.setAttribute("role", "tabpanel");
        section.setAttribute(
          "aria-labelledby",
          `${fieldIdPrefix}-access-${formIndex}-tab`,
        );
        section.hidden = method.method !== selectedAccessMethod;
      }
    }
    const sectionBody = tabbedAccessMethods
      ? element("div", {
          className: "card-body min-w-0 gap-5 p-5 sm:gap-6 sm:p-7",
        })
      : section;
    const form = element("form", {
      className: settings
        ? "card card-border min-w-0 overflow-hidden bg-base-100 shadow-sm"
        : tabbedAccessMethods ? "grid min-w-0 gap-5" : "grid min-w-0 gap-4",
    });
    form.action = flow.action;
    form.method = "post";
    form.autocomplete = "on";
    let settingsContent = null;
    let settingsActions = null;
    let registrationActions = null;
    if (settings) {
      const copy = settingsMethodCopy(method.method);
      form.dataset.accountSettingsMethod = method.method;
      form.dataset.accountSettingsLabel = copy.navigation;
      form.dataset.accountSettingsHasError = method.nodes.some((node) => (
        node.messages.some((message) => message.type === "error")
      )) ? "true" : "false";
      settingsContent = element("div", {
        className: "card-body gap-6",
      });
      const methodHeading = element("div", { className: "grid gap-2" });
      methodHeading.append(
        element("h2", { className: "font-semibold text-base-content", text: copy.title }),
        element("p", { className: "text-sm text-base-content/65", text: copy.detail }),
      );
      if (method.method !== "profile") settingsContent.append(methodHeading);
      const removalHelp = accountFirstFactorRemovalHelp(method);
      if (removalHelp) {
        const notice = element("p", {
          className: "alert alert-info text-sm",
          text: removalHelp,
        });
        notice.dataset.accountFirstFactorRemovalBlocked = "";
        settingsContent.append(notice);
      }
      settingsActions = element("div", {
        className:
          "card-actions min-h-16 justify-start border-t border-base-300 bg-base-200/50 px-5 py-4 sm:px-6",
      });
    } else {
      sectionBody.append(element("h2", {
        className: registration
          ? "text-lg font-semibold text-base-content"
          : "font-semibold text-base-content",
        text: methodTitle(flow.view, method.method),
      }));
      if (registration) {
        registrationActions = element("div", {
          className:
            "flex min-h-12 flex-wrap items-center justify-end gap-3 border-t border-base-300 pt-5",
        });
      }
    }
    if (flow.view === "login" && method.method === "oidc") {
      form.append(hiddenInput(DISCORD_LOGIN_REGISTRATION_GUARD_CONTROL));
    }
    if (flow.view === "registration" && method.method === "oidc") {
      form.append(hiddenInput(DISCORD_OIDC_CONSENT_CONTROL));
    }
    const omittedBlankTraits = [];
    if (
      flow.view === "registration"
      || (flow.view === "settings" && method.method === "profile")
    ) {
      omittedBlankTraits.push("traits.display_name");
    }
    if (flow.view === "registration") {
      omittedBlankTraits.push("traits.avatar_url", "traits.avatar_color");
    }
    if (flow.view === "registration" && method.method === "oidc") {
      omittedBlankTraits.push("traits.username");
    }
    if (omittedBlankTraits.length > 0) {
      omitBlankTraits(form, omittedBlankTraits);
    }
    if (
      flow.view === "registration"
      || (flow.view === "settings" && method.method === "profile")
    ) {
      omitBlankEmailAddresses(form);
    }
    const controls = element("div", {
      className: method.method === "profile"
        ? "grid min-w-0 grid-cols-1 gap-5 sm:grid-cols-2"
        : "grid min-w-0 max-w-2xl grid-cols-1 gap-4",
    });
    const passkeyRemovals = settings && method.method === "passkey"
      ? method.nodes.filter((node) => node.kind === "input" && node.name === "passkey_remove")
      : [];
    const passkeyList = passkeyRemovals.length > 0
      ? element("div", { className: "grid min-w-0 gap-3" })
      : null;
    if (passkeyList) {
      passkeyList.dataset.accountPasskeyList = "";
      controls.append(passkeyList);
    }
    let passkeyIndex = 0;
    const webauthnRemovals = settings && method.method === "webauthn"
      ? method.nodes.filter((node) => node.kind === "input" && node.name === "webauthn_remove")
      : [];
    const webauthnList = webauthnRemovals.length > 0
      ? element("div", { className: "grid min-w-0 gap-3" })
      : null;
    if (webauthnList) {
      webauthnList.dataset.accountWebauthnList = "";
      controls.append(webauthnList);
    }
    let webauthnIndex = 0;
    for (const [nodeIndex, control] of method.nodes.entries()) {
      let messages = null;
      if (control.kind === "image") {
        controls.append(securityImage(control));
        messages = nodeMessages(control.messages);
      } else if (control.kind === "text") {
        controls.append(securityText(control));
        messages = nodeMessages(control.messages);
      } else if (control.kind === "input" && control.type === "hidden") {
        form.append(hiddenInput(control));
        messages = nodeMessages(control.messages);
      } else if (control.kind === "input" && control.name === "traits.emails") {
        controls.append(emailAddressCollection(method, {
          emailAddresses,
          fieldIdPrefix: `${fieldIdPrefix}-${formIndex}`,
        }));
        messages = null;
      } else if (
        control.kind === "input"
        && /^traits\.emails\.(?:0|[1-9][0-9]*)$/u.test(control.name)
      ) {
        messages = null;
      } else if (control.kind === "input" && control.name === "traits.avatar_url") {
        if (settings && method.method === "profile") {
          const picker = element("fishy-account-avatar-picker", {
            className: "block sm:col-span-2",
          });
          picker.fieldName = control.name;
          picker.colorFieldName = "traits.avatar_color";
          picker.value = control.value;
          picker.color = profileNodeValue(method, "traits.avatar_color");
          picker.discordAvatarUrl = discordAvatarUrl;
          picker.discordImagesAllowed = discordImagesAllowed;
          controls.append(picker);
        } else {
          form.append(hiddenInput(control));
        }
        messages = nodeMessages(control.messages);
      } else if (control.kind === "input" && control.name === "traits.avatar_color") {
        if (!(settings && method.method === "profile")) {
          form.append(hiddenInput(control));
        }
        messages = nodeMessages(control.messages);
      } else if (
        control.kind === "input"
        && ["submit", "button"].includes(control.type)
      ) {
        if (control.name === "passkey_remove" && passkeyList) {
          passkeyList.append(passkeyCredentialRow(
            control,
            passkeyIndex,
            flow.view,
            { awaitingCode, fieldIdPrefix, passkeyLabels },
          ));
          passkeyIndex += 1;
        } else if (control.name === "webauthn_remove" && webauthnList) {
          webauthnList.append(webauthnCredentialRow(
            control,
            webauthnIndex,
            flow.view,
            { awaitingCode },
          ));
          webauthnIndex += 1;
        } else {
          (settingsActions ?? registrationActions ?? controls).append(
            controlButton(control, flow.view, { awaitingCode }),
          );
        }
        messages = nodeMessages(control.messages);
      } else if (control.kind === "input") {
        if (
          flow.view === "verification"
          && control.name === "email"
          && control.type === "email"
          && knownVerificationEmail
        ) {
          form.append(hiddenInput({ ...control, value: knownVerificationEmail }));
          const email = element("div", {
            className: "grid gap-1 rounded-box border border-base-300 bg-base-200/45 px-4 py-3",
          });
          email.append(
            element("span", {
              className: "text-xs font-medium uppercase tracking-wide text-base-content/55",
              text: translate("account.email.address", "Email address"),
            }),
            element("span", {
              className: "break-all text-sm font-medium text-base-content",
              text: knownVerificationEmail,
            }),
          );
          controls.append(email);
          messages = nodeMessages(control.messages);
          if (messages) controls.append(messages);
          continue;
        }
        const optionalPasskeyIdentifier = flow.view === "login"
          && method.method === "passkey"
          && control.name === "identifier";
        const optionalDisplayName = ["registration", "settings"].includes(flow.view)
          && control.name === "traits.display_name";
        const optionalDiscordHandle = flow.view === "registration"
          && method.method === "oidc"
          && control.name === "traits.username";
        const inputOptions = {
          messageId: `${fieldIdPrefix}-${formIndex}-${nodeIndex}-messages`,
          view: flow.view,
          required: optionalPasskeyIdentifier || optionalDisplayName || optionalDiscordHandle
            ? false
            : control.required,
          labelText: optionalPasskeyIdentifier
            ? translate(
                "account.field.identifier_optional",
                "Account handle or recovery email (optional)",
              )
            : optionalDiscordHandle
              ? translate("account.field.handle_optional", "Account handle (optional)")
              : optionalDisplayName
                ? translate("account.field.display_name_optional", "Display name (optional)")
                : fieldLabel(control),
          helpText: optionalPasskeyIdentifier
            ? translate(
                "account.passkey.identifier_help",
                "Usually you can choose the account in your device’s passkey prompt.",
              )
            : optionalDiscordHandle
              ? translate(
                  "account.registration.discord_handle_help",
                  "Leave blank to use your Discord username.",
                )
              : optionalDisplayName
                ? translate(
                    "account.field.display_name_help",
                    "When blank, FishyStuff shows your account handle instead.",
                  )
                : "",
        };
        controls.append(optionalDisplayName
          ? displayNameControl(control, inputOptions)
          : controlInput(control, inputOptions));
      }
      if (messages) {
        (settingsActions ?? controls).append(messages);
      }
    }
    if (flow.view === "registration") {
      controls.append(registrationLegalAcceptance(formIndex));
    }
    if (settings) {
      if (method.method === "profile") {
        arrangeProfileSettings(controls, method, {
          discordImagesAllowed,
        });
      }
      settingsContent.append(controls);
      form.append(settingsContent, settingsActions);
    } else {
      if (flow.view === "registration" && !hasRequiredLegalDocuments()) {
        const unavailable = element("fieldset", { className: "min-w-0" });
        unavailable.disabled = true;
        unavailable.append(controls);
        if (registrationActions?.childElementCount) unavailable.append(registrationActions);
        form.append(unavailable);
        form.addEventListener("submit", (event) => {
          event.preventDefault();
          event.stopImmediatePropagation();
        });
      } else {
        form.append(controls);
        if (registrationActions?.childElementCount) form.append(registrationActions);
      }
      sectionBody.append(form);
    }
    if (["passkey", "webauthn"].includes(method.method)) {
      const webauthnStatus = element("p", {
        className: "text-sm text-base-content/60",
        text: method.method === "passkey"
          ? translate("account.passkey.loading", "Loading passkey support…")
          : translate("account.webauthn.loading", "Loading security-key support…"),
      });
      webauthnStatus.dataset.webauthnStatus = "";
      webauthnStatus.setAttribute("role", "status");
      (settingsContent ?? sectionBody).append(webauthnStatus);
    }
    if (settings) {
      methodsRoot.append(form);
    } else if (sectionBody !== section) {
      section.append(sectionBody);
      methodsRoot.append(section);
    } else {
      methodsRoot.append(section);
    }
  });
  root.append(methodsRoot);

  if (flow.completion?.continueNode) {
    const continuation = element("a", {
      className: "btn btn-primary w-fit",
      text: translate("account.action.continue", "Continue"),
    });
    continuation.href = flow.completion.continueNode.href;
    continuation.rel = "nofollow";
    root.append(continuation);
  }
  const navigation = flowNavigation(flow, context);
  if (navigation) {
    root.append(navigation);
  }
  return root;
}

export function identityErrorPresentation(error = null) {
  const conflict = error?.code === 409
    || /\b(?:already used by|already (?:exists|connected)|conflicts? with)\b/i.test(
      String(error?.message ?? ""),
    );
  const unsupported = error instanceof IdentityBrowserContractError
    && error.kind === "unsupported";
  let detail = safeProviderCopy({ type: "error", text: error?.message });
  if (conflict) {
    detail = translate(
      "account.error.connection_conflict",
      "That sign-in method is already connected to another account. Sign in to that account first if you want to change its connections.",
    );
  } else if (unsupported) {
    detail = translate(
      "account.error.unsupported",
      "This account page includes controls this FishyStuff version cannot safely display.",
    );
  }
  return Object.freeze({
    title: conflict
      ? translate("account.error.connection_title", "Account connection needs attention")
      : translate("account.error.title", "Account access problem"),
    detail,
  });
}

export function createLoginExpiredView(context) {
  const root = element("div", { className: "grid gap-4" });
  const status = element("p", {
    className: "alert alert-error text-sm",
    text: translate(
      "account.error.login_expired",
      "Your login expired. Sign in again to continue.",
    ),
  });
  status.setAttribute("role", "alert");
  const link = element("a", {
    className: "btn btn-primary w-fit",
    text: translate("account.action.sign_in_again", "Sign in again"),
  });
  link.href = context.loginStartUrl;
  link.rel = "nofollow";
  link.dataset.accountStartFlow = "login";
  root.append(status, link);
  return root;
}
